// Shared alignment-class resolver for runtime renderers (Story saas-1.8).
// Always returns a concrete Tailwind class (never empty), to avoid surprising
// CSS inheritance from ancestor wrappers. Default fallback is 'text-left'.
//
// Story saas-1.10 cleanup: FieldAlignment type is now imported from types/forms.ts
// (the canonical location) instead of being declared locally. Eliminates the
// duplication flagged by @qa Quinn in the saas-1.9 QA gate.

import type { FieldAlignment, FieldType } from '../types/forms';

export type { FieldAlignment };

export const getAlignmentClass = (
  alignment?: FieldAlignment
): 'text-left' | 'text-center' | 'text-right' => {
  if (alignment === 'center') return 'text-center';
  if (alignment === 'right') return 'text-right';
  return 'text-left';
};

// Story saas-1.11: single source of truth for type-aware alignment resolution.
// Resolves to the explicit `alignment` if set; otherwise falls back to 'center'
// for welcome_screen/thank_you_screen (backward compat — forms created
// pre-saas-1.11 rendered centered via hardcoded `text-center` inside
// WelcomeScreenField/ThankYouScreenField) and to 'left' for all other types.
//
// Used by:
// - getScreenAlignmentClass (runtime render: SlidePreview, FormPublic)
// - SlidePropertiesPanel (UI "selected" state for the 3 alignment buttons)
// Centralizing here eliminates UI↔render divergence (Quinn 2 gate finding).
export const getEffectiveAlignment = (
  type: FieldType,
  alignment?: FieldAlignment
): FieldAlignment => {
  if (alignment) return alignment;
  if (type === 'welcome_screen' || type === 'thank_you_screen') return 'center';
  return 'left';
};

// Story saas-1.11: type-aware wrapper alignment for special screens.
// Delegates to getEffectiveAlignment for resolution, then to
// getAlignmentClass for the final Tailwind class.
export const getScreenAlignmentClass = (
  type: FieldType,
  alignment?: FieldAlignment
): 'text-left' | 'text-center' | 'text-right' => {
  return getAlignmentClass(getEffectiveAlignment(type, alignment));
};
